import { useQuery } from 'react-query';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { CircularProgress } from '@mui/material';

import { AdminDashboardApi } from '@app/domain/dashboard/api/AdminDashboardApi';
import { clsxm } from '@app/styles/clsxm';

import { DashboardCard } from '../shared/DashboardCard';
import { DASHBOARD_COLOR } from '../shared/dashboardColors';

const fmt = (n: number) => n.toLocaleString('nl-NL');
const nowLabel = () => {
  const now = new Date();
  const weekday = now.toLocaleDateString('nl-NL', { weekday: 'long' });
  const date = now.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' });
  const time = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  return `Vandaag tot nu toe — ${weekday} ${date}, ${time}`;
};

// admin-dashboard-opdracht.md §4.1. Only rendered when the "Vandaag" preset is active - see
// AdminDashboardPage's own isToday check. comparisonLabel chips stay neutral: the backend only
// exposes a pre-formatted string here (AdminDashboardFacadeImpl.percentLabel), not a raw
// up/down/good/bad signal, and inventing tone from the label text would risk getting a
// row like "Opzeggingen" backwards - §5's own rule is never colour without something real
// backing it.
export const DashboardTodayBand: React.FC = () => {
  const query = useQuery(['admin-dashboard-today'], () => AdminDashboardApi.getToday());

  if (query.isLoading) {
    return (
      <div className="flex h-[200px] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (!query.data) {
    return null;
  }

  const { hourly, figures } = query.data;

  return (
    <div>
      <h2 className="mb-2.5 mt-1.5 text-[12px] font-bold uppercase tracking-wide text-gray-400">{nowLabel()}</h2>
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
        <DashboardCard title="Per uur" subtitle="Aanmeldingen vandaag, tegen het gemiddelde van dezelfde weekdag">
          <div className="mb-2 flex flex-wrap gap-4 text-[12.5px] text-gray-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-[9px] w-[9px] rounded-sm" style={{ background: DASHBOARD_COLOR.cat1 }} />
              Vandaag
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-[9px] w-[9px] rounded-sm bg-[#c9ced6]" />
              Gemiddelde weekdag
            </span>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 8, right: 8 }}>
                <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                <XAxis
                  dataKey="hour"
                  tickFormatter={(h: number) => String(h).padStart(2, '0')}
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  stroke={DASHBOARD_COLOR.muted}
                />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} width={28} />
                <RTooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) {
                      return null;
                    }
                    const row = payload[0].payload as typeof hourly[number];
                    const h = String(label).padStart(2, '0');
                    return (
                      <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] leading-relaxed text-white">
                        <b>
                          {h}:00 – {h}:59
                        </b>
                        {row.running && <span className="opacity-70"> (loopt nog)</span>}
                        <br />
                        Vandaag: <b>{row.today}</b>
                        <br />
                        Gemiddelde weekdag: <b>{row.averageSameWeekday.toFixed(1)}</b>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="averageSameWeekday" fill="#c9ced6" radius={[3, 3, 0, 0]} barSize={11} />
                <Bar dataKey="today" radius={[3, 3, 0, 0]} barSize={11}>
                  {hourly.map((r) => (
                    <Cell key={r.hour} fill={DASHBOARD_COLOR.cat1} fillOpacity={r.running ? 0.45 : 1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-[11.5px] text-gray-400">
            Het uur waar we nu in zitten is niet compleet en is daarom lichter.
          </p>
        </DashboardCard>

        <DashboardCard title="Vandaag in cijfers" subtitle="Sinds middernacht Nederlandse tijd">
          <table className="w-full text-left text-[13.5px]">
            <tbody>
              {figures.map((f, i) => (
                <tr key={f.label} className={clsxm('border-b border-[#eef0f3]', i % 2 === 1 && 'bg-gray-50')}>
                  <td className="py-2 pl-1 text-gray-600">{f.label}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-800">{fmt(f.value)}</td>
                  <td className="py-2 pr-1">
                    <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11.5px] text-gray-500">
                      {f.comparisonLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DashboardCard>
      </div>
    </div>
  );
};
